import { useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Folder, Note, Chat, Users, Paperclip, ChartBar, CalendarBlank,
  Coins, Pulse, SquaresFour, Trash, Plus, X, CircleNotch, ListChecks,
  PaperPlaneRight, UserPlus, Check,
} from "@phosphor-icons/react";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import UserLink from "../components/UserLink";
import TasksPage from "./TasksPage";

/**
 * ProjectWorkspace — every project opens into its own multi-tab space.
 * Tabs: Overview · Board · Notes · Files · Chat · Members · Timeline · Budget · Activity
 */

const TABS = [
  { key: "overview",  label: "Overview",  icon: SquaresFour },
  { key: "board",     label: "Board",     icon: ListChecks },
  { key: "notes",     label: "Notes",     icon: Note },
  { key: "files",     label: "Files",     icon: Paperclip },
  { key: "chat",      label: "Chat",      icon: Chat },
  { key: "members",   label: "Members",   icon: Users },
  { key: "timeline",  label: "Timeline",  icon: CalendarBlank },
  { key: "budget",    label: "Budget",    icon: Coins },
  { key: "activity",  label: "Activity",  icon: Pulse },
];


export default function ProjectWorkspace() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${pid}`);
      setProject(data);
    } catch (ex) {
      if (ex?.response?.status === 404) setNotFound(true);
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const isOwner = project && user && project.owner_id === user.user_id;
  const deleteProject = async () => {
    if (!window.confirm("Delete this project and all its data? This cannot be undone.")) return;
    await api.delete(`/projects/${pid}`);
    navigate("/app/projects");
  };

  if (loading) return (
    <div className="max-w-6xl mx-auto rounded-2xl p-10 text-center" data-testid="project-loading"
         style={{ border: "1px solid rgba(212,175,55,0.35)", background: "rgba(0,0,0,0.4)" }}>
      <CircleNotch size={22} className="animate-spin text-[#FFD700] mx-auto"/>
    </div>
  );

  if (notFound || !project) return (
    <div className="max-w-3xl mx-auto rounded-2xl p-10 text-center" data-testid="project-404"
         style={{ border: "1px solid rgba(255,93,115,0.4)", background: "rgba(0,0,0,0.5)" }}>
      <div className="font-display text-[24px] text-[#FF5D73]">Project not found</div>
      <Link to="/app/projects" className="mt-4 inline-block text-[11px] uppercase tracking-widest text-[#FFD700]">
        Back to Projects
      </Link>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-5" data-testid="project-workspace">
      <Link
        to="/app/projects"
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#F2DC8C]/70 hover:text-[#FFD700]"
        data-testid="project-back">
        <ArrowLeft size={12}/> All Projects
      </Link>

      {/* Header */}
      <div className="rounded-2xl p-5 flex flex-wrap items-start gap-4"
           style={{
             background: "linear-gradient(180deg, rgba(26,18,6,0.85) 0%, rgba(5,3,0,0.95) 100%)",
             border: "1px solid rgba(255,215,0,0.4)",
             boxShadow: "0 12px 40px rgba(0,0,0,0.55)",
           }}>
        <span className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
          <Folder size={22} weight="fill" color="#0a0703"/>
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-[28px] leading-tight"
              style={{
                background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #8a6a00 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>
            {project.name}
          </h1>
          {project.description && (
            <p className="mt-1 text-[13px] text-[#F2DC8C]/75 leading-relaxed">{project.description}</p>
          )}
          <div className="mt-2 text-[10px] uppercase tracking-widest text-[#F2DC8C]/55">
            {project.members?.length || 1} member{project.members?.length === 1 ? "" : "s"} ·
            Created {new Date(project.created_at).toLocaleDateString()}
          </div>
        </div>
        {isOwner && (
          <button onClick={deleteProject}
                  data-testid="project-delete"
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#FF5D73] hover:text-red-400">
            <Trash size={11}/> Delete
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto no-scrollbar pb-1" data-testid="project-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
                    data-testid={`tab-${t.key}`}
                    className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-xl font-display uppercase text-[10px] tracking-[0.22em] shrink-0 transition-all"
                    style={active
                      ? { background: "linear-gradient(90deg, rgba(255,215,0,0.16), rgba(212,175,55,0.05))",
                          border: "1px solid rgba(255,215,0,0.55)", color: "#FFD700",
                          boxShadow: "0 0 14px rgba(255,215,0,0.18)" }
                      : { border: "1px solid rgba(212,175,55,0.3)", color: "#F2DC8C" }}>
              <Icon size={11} weight={active ? "fill" : "regular"}/> {t.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div>
        {tab === "overview"  && <TabOverview project={project} onReload={load}/>}
        {tab === "board"     && <TasksPage projectId={pid} projectName={project.name}/>}
        {tab === "notes"     && <TabNotes pid={pid}/>}
        {tab === "files"     && <TabFiles pid={pid}/>}
        {tab === "chat"      && <TabChat pid={pid}/>}
        {tab === "members"   && <TabMembers project={project} onReload={load} isOwner={isOwner}/>}
        {tab === "timeline"  && <ComingSoonTab title="Timeline" blurb="Gantt-style timeline with milestones + dependencies coming in the next drop."/>}
        {tab === "budget"    && <ComingSoonTab title="Budget"   blurb="Track spend across a project — categories, invoices, and paid vs pending totals."/>}
        {tab === "activity"  && <TabActivity pid={pid}/>}
      </div>
    </div>
  );
}


function ComingSoonTab({ title, blurb }) {
  return (
    <div className="rounded-2xl p-10 text-center"
         data-testid="tab-stub"
         style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.35)" }}>
      <div className="font-display text-[24px] text-[#FFD700]">{title}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#F2DC8C]/60 mt-1">Coming soon</div>
      <p className="mt-3 text-[13px] text-[#F2DC8C]/75 max-w-lg mx-auto">{blurb}</p>
    </div>
  );
}


function TabOverview({ project, onReload }) {
  const [stats, setStats] = useState({ total: 0, done: 0, doing: 0, todo: 0 });
  const [recentNotes, setRecentNotes] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [tRes, nRes, aRes] = await Promise.all([
          api.get(`/tasks`, { params: { project_id: project.project_id } }),
          api.get(`/projects/${project.project_id}/notes`),
          api.get(`/projects/${project.project_id}/activity`),
        ]);
        const ts = tRes.data || [];
        setStats({
          total: ts.length,
          done: ts.filter((t) => t.status === "done").length,
          doing: ts.filter((t) => t.status === "doing").length,
          todo: ts.filter((t) => t.status === "todo").length,
        });
        setRecentNotes((nRes.data || []).slice(0, 3));
        setRecentActivity((aRes.data || []).slice(0, 6));
      } catch { /* ignore */ }
    })();
  }, [project.project_id]);
  const pct = stats.total ? Math.round((stats.done / stats.total) * 100) : 0;
  return (
    <div className="space-y-4" data-testid="tab-overview">
      <div className="rounded-2xl p-5" style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.4)" }}>
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[#F2DC8C]/70 mb-2">
          <span>Progress</span>
          <span className="tabular-nums text-[#FFD700] font-display text-[15px]">{pct}%</span>
        </div>
        <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(212,175,55,0.15)" }}>
          <div className="h-full" style={{
            width: `${pct}%`,
            background: "linear-gradient(90deg, #FFE58A, #FFD700 60%, #D4AF37)",
            boxShadow: "0 0 8px rgba(255,215,0,0.4)",
          }}/>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3 text-center">
          <MiniStat label="To Do" value={stats.todo}/>
          <MiniStat label="Doing" value={stats.doing}/>
          <MiniStat label="Done"  value={stats.done}/>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl p-4" style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.4)" }}>
          <div className="text-[10px] uppercase tracking-widest text-[#F2DC8C]/70 mb-2">Recent Notes</div>
          {recentNotes.length === 0 ? (
            <div className="text-[12px] text-[#F2DC8C]/50">No notes yet.</div>
          ) : recentNotes.map((n) => (
            <div key={n.note_id} className="py-2 border-t first:border-t-0" style={{ borderColor: "rgba(212,175,55,0.15)" }}>
              <div className="text-[13px] font-display text-[#FFD700] truncate">{n.title || "Untitled"}</div>
              <div className="text-[11px] text-[#F2DC8C]/70 line-clamp-2">{n.body}</div>
            </div>
          ))}
        </div>
        <div className="rounded-2xl p-4" style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.4)" }}>
          <div className="text-[10px] uppercase tracking-widest text-[#F2DC8C]/70 mb-2">Recent Activity</div>
          {recentActivity.length === 0 ? (
            <div className="text-[12px] text-[#F2DC8C]/50">Nothing here yet.</div>
          ) : recentActivity.map((a) => (
            <div key={a.activity_id} className="py-1.5 text-[11px] text-[#F2DC8C]/85 border-t first:border-t-0"
                 style={{ borderColor: "rgba(212,175,55,0.15)" }}>
              <span className="text-[#FFD700]">{a.kind.replace(".", " ")}</span>
              {a.meta?.title ? ` — ${a.meta.title}` : ""}
              {a.meta?.name ? ` — ${a.meta.name}` : ""}
              <span className="ml-1 text-[#F2DC8C]/40">
                {new Date(a.created_at).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg py-2"
         style={{ border: "1px solid rgba(212,175,55,0.3)", background: "rgba(0,0,0,0.3)" }}>
      <div className="text-[9px] uppercase tracking-widest text-[#F2DC8C]/60">{label}</div>
      <div className="font-display text-[20px] text-[#FFD700] tabular-nums">{value}</div>
    </div>
  );
}


function TabNotes({ pid }) {
  const [notes, setNotes] = useState([]);
  const [editing, setEditing] = useState(null); // {mode:'new'|'edit', note?}
  const load = async () => {
    try { const { data } = await api.get(`/projects/${pid}/notes`); setNotes(data || []); } catch {}
  };
  useEffect(() => { load(); }, [pid]);
  const save = async (form) => {
    if (editing.note) await api.put(`/projects/${pid}/notes/${editing.note.note_id}`, form);
    else await api.post(`/projects/${pid}/notes`, form);
    setEditing(null); load();
  };
  const remove = async (nid) => {
    if (!window.confirm("Delete this note?")) return;
    await api.delete(`/projects/${pid}/notes/${nid}`); load();
  };
  return (
    <div className="space-y-3" data-testid="tab-notes">
      <div className="flex justify-end">
        <button onClick={() => setEditing({ mode: "new" })}
                data-testid="notes-new"
                className="inline-flex items-center gap-2 h-9 px-3 rounded-xl font-display uppercase text-[10px] tracking-[0.22em]"
                style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
          <Plus size={11} weight="bold"/> New Note
        </button>
      </div>
      {notes.length === 0 ? (
        <div className="rounded-2xl p-10 text-center text-[13px] text-[#F2DC8C]/60"
             style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.35)" }}>
          No notes yet.
        </div>
      ) : notes.map((n) => (
        <div key={n.note_id} className="rounded-xl p-4 group"
             data-testid={`note-${n.note_id}`}
             style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(212,175,55,0.35)" }}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="font-display text-[16px] text-[#FFD700]">{n.title || "Untitled"}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#F2DC8C]/50 mt-0.5">
                {n.author_name} · {new Date(n.updated_at).toLocaleString()}
              </div>
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => setEditing({ mode: "edit", note: n })}
                      data-testid={`note-edit-${n.note_id}`}
                      className="text-[10px] uppercase tracking-widest text-[#F2DC8C] hover:text-[#FFD700]">
                Edit
              </button>
              <button onClick={() => remove(n.note_id)}
                      data-testid={`note-delete-${n.note_id}`}
                      className="text-[10px] uppercase tracking-widest text-[#FF5D73] hover:text-red-400">
                Delete
              </button>
            </div>
          </div>
          <div className="mt-2 text-[13px] text-[#F2DC8C] whitespace-pre-wrap leading-relaxed">
            {n.body}
          </div>
        </div>
      ))}
      <AnimatePresence>
        {editing && (
          <NoteModal note={editing.note} onClose={() => setEditing(null)} onSave={save}/>
        )}
      </AnimatePresence>
    </div>
  );
}

function NoteModal({ note, onClose, onSave }) {
  const [title, setTitle] = useState(note?.title || "");
  const [body, setBody]   = useState(note?.body || "");
  const [busy, setBusy]   = useState(false);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={onClose}
                className="fixed inset-0 z-[110] flex items-center justify-center p-4"
                style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(14px)" }}
                data-testid="note-modal">
      <motion.form initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                   onClick={(e) => e.stopPropagation()}
                   onSubmit={async (e) => { e.preventDefault(); setBusy(true); try { await onSave({ title: title.trim() || null, body }); } finally { setBusy(false); } }}
                   className="w-full max-w-lg rounded-2xl overflow-hidden"
                   style={{
                     background: "linear-gradient(180deg, rgba(20,14,4,0.98) 0%, rgba(5,3,0,1) 100%)",
                     border: "1.5px solid rgba(255,215,0,0.55)",
                     boxShadow: "0 30px 80px rgba(0,0,0,0.75)",
                   }}>
        <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
          <Note size={16} weight="fill" className="text-[#FFD700]"/>
          <div className="font-display text-[16px] text-[#FFD700]">{note ? "Edit Note" : "New Note"}</div>
          <button type="button" onClick={onClose} className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ border: "1px solid rgba(212,175,55,0.5)" }}>
            <X size={13} className="text-[#FFD700]"/>
          </button>
        </div>
        <div className="p-5 space-y-3">
          <input data-testid="note-title" value={title} onChange={(e) => setTitle(e.target.value)}
                 placeholder="Title (optional)"
                 className="w-full bg-transparent px-3 py-2 rounded-lg text-[15px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/40 focus:outline-none"
                 style={{ border: "1px solid rgba(212,175,55,0.4)" }}/>
          <textarea data-testid="note-body" value={body} onChange={(e) => setBody(e.target.value)}
                    rows={9} placeholder="Write anything…"
                    className="w-full bg-transparent px-3 py-2 rounded-lg text-[13px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/40 focus:outline-none"
                    style={{ border: "1px solid rgba(212,175,55,0.4)" }}/>
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
          <button type="button" onClick={onClose}
                  className="px-4 h-10 rounded-xl text-[11px] uppercase tracking-widest text-[#F2DC8C]"
                  style={{ border: "1px solid rgba(212,175,55,0.45)" }}>Cancel</button>
          <button type="submit" disabled={busy || !body.trim()} data-testid="note-save"
                  className="px-4 h-10 rounded-xl font-display uppercase text-[11px] tracking-[0.22em] disabled:opacity-40"
                  style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
}


function TabFiles({ pid }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const load = async () => {
    try { const { data } = await api.get(`/projects/${pid}/files`); setFiles(data || []); } catch {}
  };
  useEffect(() => { load(); }, [pid]);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      // Reuse existing image uploader for small files; larger files should
      // fall back to chunked-upload but for phase-1 keeping it lean.
      const { data } = await api.post("/uploads/image", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/projects/${pid}/files`, {
        file_id: data.file_id, filename: f.name, content_type: f.type, size: f.size,
      });
      load();
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const remove = async (aid) => {
    if (!window.confirm("Detach this file?")) return;
    await api.delete(`/projects/${pid}/files/${aid}`); load();
  };

  return (
    <div className="space-y-3" data-testid="tab-files">
      <div className="flex justify-end">
        <input ref={inputRef} type="file" className="hidden" onChange={onPick} data-testid="file-input"/>
        <button onClick={() => inputRef.current?.click()} disabled={uploading}
                data-testid="file-upload"
                className="inline-flex items-center gap-2 h-9 px-3 rounded-xl font-display uppercase text-[10px] tracking-[0.22em] disabled:opacity-50"
                style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
          <Paperclip size={11}/> {uploading ? "Uploading…" : "Attach File"}
        </button>
      </div>
      {files.length === 0 ? (
        <div className="rounded-2xl p-10 text-center text-[13px] text-[#F2DC8C]/60"
             style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.35)" }}>
          No files attached yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {files.map((f) => (
            <div key={f.attach_id} data-testid={`file-${f.attach_id}`}
                 className="rounded-xl p-3 group"
                 style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(212,175,55,0.35)" }}>
              <a href={`${API}/files/${f.file_id}`} target="_blank" rel="noopener noreferrer"
                 className="block aspect-video rounded-lg overflow-hidden bg-black/40 mb-2 flex items-center justify-center">
                {f.content_type?.startsWith("image/") ? (
                  <img src={`${API}/files/${f.file_id}`} alt="" className="w-full h-full object-cover"/>
                ) : (
                  <Paperclip size={26} className="text-[#FFD700]"/>
                )}
              </a>
              <div className="text-[11px] text-[#F2DC8C] truncate">{f.filename || f.file_id}</div>
              <div className="text-[9px] uppercase tracking-widest text-[#F2DC8C]/55 mt-0.5">
                {f.attached_by_name}
              </div>
              <button onClick={() => remove(f.attach_id)}
                      data-testid={`file-remove-${f.attach_id}`}
                      className="mt-2 text-[9px] uppercase tracking-widest text-[#FF5D73] opacity-0 group-hover:opacity-100 transition-opacity">
                Detach
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function TabChat({ pid }) {
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const load = async () => {
    try { const { data } = await api.get(`/projects/${pid}/messages`); setMsgs(data || []); } catch {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000); // simple polling; Phase 2 messages upgrade adds WS.
    return () => clearInterval(t);
  }, [pid]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs]);
  const send = async (e) => {
    e?.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${pid}/messages`, { body });
      setMsgs((p) => [...p, data]);
      setBody("");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl overflow-hidden flex flex-col" data-testid="tab-chat"
         style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.4)", height: "60vh", minHeight: 380 }}>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {msgs.length === 0 ? (
          <div className="text-center text-[13px] text-[#F2DC8C]/55 pt-16">Start the conversation.</div>
        ) : msgs.map((m) => (
          <div key={m.msg_id} className="flex gap-2 items-start" data-testid={`msg-${m.msg_id}`}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 overflow-hidden"
                 style={{ border: "1px solid #D4AF37" }}>
              {m.author_avatar ? (
                <img src={`${API}/files/${m.author_avatar}`} alt="" className="w-full h-full object-cover"/>
              ) : (
                <span className="text-[11px] font-display text-[#FFD700]">
                  {(m.author_name || "?").slice(0, 1).toUpperCase()}
                </span>
              )}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#F2DC8C]/55">
                {m.author_name} · {new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
              </div>
              <div className="text-[13px] text-[#F2DC8C] whitespace-pre-wrap break-words">{m.body}</div>
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={send} className="p-3 border-t flex gap-2"
            style={{ borderColor: "rgba(212,175,55,0.35)" }}>
        <input data-testid="chat-input" value={body}
               onChange={(e) => setBody(e.target.value.slice(0, 4000))}
               placeholder="Message the project…"
               className="flex-1 bg-transparent px-3 py-2 rounded-lg text-[13px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/40 focus:outline-none"
               style={{ border: "1px solid rgba(212,175,55,0.4)" }}/>
        <button type="submit" disabled={busy || !body.trim()} data-testid="chat-send"
                className="w-10 h-10 rounded-lg flex items-center justify-center disabled:opacity-40"
                style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
          <PaperPlaneRight size={14} weight="fill" color="#0a0703"/>
        </button>
      </form>
    </div>
  );
}


function TabMembers({ project, onReload, isOwner }) {
  const [username, setUsername] = useState("");
  const [err, setErr] = useState("");
  const [friends, setFriends] = useState([]);

  useEffect(() => {
    if (!isOwner) return;
    (async () => {
      try { const { data } = await api.get("/friends"); setFriends(data || []); } catch {}
    })();
  }, [isOwner]);

  const add = async (usernameToAdd) => {
    setErr("");
    try {
      await api.post(`/projects/${project.project_id}/members`, { username: usernameToAdd });
      setUsername("");
      onReload();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not add member.");
    }
  };
  const submit = async (e) => { e?.preventDefault(); if (username.trim()) await add(username.trim()); };
  const remove = async (uid) => {
    if (!window.confirm("Remove this member from the project?")) return;
    await api.delete(`/projects/${project.project_id}/members/${uid}`);
    onReload();
  };

  const memberIds = new Set((project.members || []).map((m) => m.user_id));
  const friendsNotYetIn = friends.filter((f) => !memberIds.has(f.user_id));

  return (
    <div className="space-y-3" data-testid="tab-members">
      {isOwner && (
        <>
          {friendsNotYetIn.length > 0 && (
            <div className="rounded-xl p-3"
                 style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.4)" }}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#F2DC8C]/70 mb-2">
                Add from your friends
              </div>
              <div className="flex flex-wrap gap-2">
                {friendsNotYetIn.map((f) => (
                  <button key={f.user_id} type="button" onClick={() => add(f.username)}
                          data-testid={`add-friend-${f.username}`}
                          className="inline-flex items-center gap-2 pl-1.5 pr-3 py-1 rounded-full transition-transform active:scale-95"
                          style={{ background: "rgba(255,215,0,0.08)", border: "1px solid rgba(255,215,0,0.5)" }}>
                    <span className="w-6 h-6 rounded-full flex items-center justify-center overflow-hidden"
                          style={{ border: "1px solid #D4AF37" }}>
                      {f.avatar_image ? (
                        <img src={`${API}/files/${f.avatar_image}`} alt="" className="w-full h-full object-cover"/>
                      ) : (
                        <span className="text-[10px] font-display text-[#FFD700]">
                          {(f.name || f.username || "?").slice(0, 1).toUpperCase()}
                        </span>
                      )}
                    </span>
                    <span className="text-[11px] text-[#F2DC8C]">
                      {f.name?.split(" ")[0] || "@" + f.username}
                    </span>
                    <UserPlus size={11} className="text-[#FFD700]"/>
                  </button>
                ))}
              </div>
            </div>
          )}
          <form onSubmit={submit} className="rounded-xl p-3 flex gap-2 items-center"
                style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.4)" }}>
            <UserPlus size={14} className="text-[#FFD700]"/>
            <input data-testid="member-username" value={username}
                   onChange={(e) => setUsername(e.target.value)}
                   placeholder="Or add by @username"
                   className="flex-1 bg-transparent px-2 py-1 text-[13px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/40 focus:outline-none"/>
            <button type="submit" data-testid="member-add"
                    className="px-3 h-9 rounded-lg font-display uppercase text-[10px] tracking-[0.22em]"
                    style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
              Add
            </button>
          </form>
        </>
      )}
      {err && <div className="text-[11px] text-[#FF5D73]">{err}</div>}
      <div className="rounded-2xl divide-y" style={{
        background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.4)",
      }}>
        {project.members?.map((m) => (
          <div key={m.user_id} className="flex items-center gap-3 p-3"
               data-testid={`member-${m.user_id}`}
               style={{ borderColor: "rgba(212,175,55,0.15)" }}>
            <div className="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center"
                 style={{ border: "1.5px solid #D4AF37" }}>
              {m.avatar_image ? (
                <img src={`${API}/files/${m.avatar_image}`} alt="" className="w-full h-full object-cover"/>
              ) : (
                <span className="text-[13px] font-display text-[#FFD700]">
                  {(m.name || m.username || "?").slice(0, 1).toUpperCase()}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <UserLink username={m.username} className="block">
                <div className="text-[13px] text-[#F2DC8C] truncate">{m.name || "@" + m.username}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#F2DC8C]/60 truncate">@{m.username}</div>
              </UserLink>
            </div>
            {m.user_id === project.owner_id ? (
              <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded"
                    style={{ border: "1px solid rgba(255,215,0,0.55)", color: "#FFD700" }}>
                Owner
              </span>
            ) : isOwner && (
              <button onClick={() => remove(m.user_id)}
                      data-testid={`member-remove-${m.user_id}`}
                      className="text-[10px] uppercase tracking-widest text-[#FF5D73] hover:text-red-400">
                Remove
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function TabActivity({ pid }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/projects/${pid}/activity`); setItems(data || []); } catch {}
    })();
  }, [pid]);
  return (
    <div className="rounded-2xl divide-y" data-testid="tab-activity"
         style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.4)" }}>
      {items.length === 0 ? (
        <div className="p-10 text-center text-[13px] text-[#F2DC8C]/60">No activity yet.</div>
      ) : items.map((a) => (
        <div key={a.activity_id} className="p-3 flex items-start gap-2"
             data-testid={`activity-${a.activity_id}`}
             style={{ borderColor: "rgba(212,175,55,0.15)" }}>
          <span className="w-2 h-2 rounded-full mt-2 shrink-0"
                style={{ background: "#FFD700", boxShadow: "0 0 8px #FFD700" }}/>
          <div className="min-w-0 flex-1">
            <div className="text-[12px] text-[#F2DC8C]">
              <span className="text-[#FFD700] font-display">{a.kind.replace(".", " ")}</span>
              {a.meta?.title ? ` — ${a.meta.title}` : ""}
              {a.meta?.name ? ` — ${a.meta.name}` : ""}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#F2DC8C]/55 mt-0.5">
              {new Date(a.created_at).toLocaleString()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
